import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-icon';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { AuthHelper } from '../../auth/auth-helper';
import { Shop } from '../../models/shop.model';
import { VesselWoProcessingJob, VesselWoProcessingJobStatus } from '../../models/vessel-wo-processing-job.model';
import { VesselWoService } from '../../services/vessel-wo.service';
import { ToastrService } from '../../services/toastr-service';
import { downloadBlob, handleDownloadError } from '../../helper/download-helpers';
import { VesselWoUploadDrawerComponent } from '../../components/vessel-wo/vessel-wo-upload-drawer/vessel-wo-upload-drawer.component';
import { utils } from '../../helper/utils';
import { LoaderComponent } from '../../shared-components/loader/loader.component';
import { AuthPolicies } from '../../auth/auth-policies';

const TAG_APPEARANCE_BY_STATUS: Record<VesselWoProcessingJobStatus, string> = {
  Enqueued: 'neutral',
  'In Progress': 'info',
  Completed: 'success',
  Failed: 'error',
};

@Component({
  selector: 'app-vessel-wo',
  standalone: true,
  imports: [VesselWoUploadDrawerComponent, LoaderComponent],
  templateUrl: './vessel-wo.component.html',
  styleUrl: './vessel-wo.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VesselWoComponent implements OnInit {
  vesselShops: Shop[] = [];
  isUploadDrawerOpen = false;
  isUploadingFile = false;

  data: VesselWoProcessingJob[] = [];
  isLoading = false;
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 10 }, (_, i) => ({ id: i }));
  currentPage: number = 1;
  pageLimit: number = 10;
  lastPage: number | undefined;
  totalItemCount: number | undefined;

  columns: Array<TableColumn> = [
    { id: 'uploadedFileName', label: 'File Name', sortDisabled: true },
    { id: 'status', label: 'Status', sortDisabled: true },
    { id: 'totalRows', label: 'Total Rows', sortDisabled: true },
    { id: 'successCount', label: 'Success', sortDisabled: true },
    { id: 'failureCount', label: 'Failed', sortDisabled: true },
    {
      id: 'createdAt',
      label: 'Created On',
      sortDisabled: true,
      dataType: {
        type: 'string',
        formatter: (value) => utils.formatUtcTimestamp((value as string) ?? ''),
      },
    },
    { id: 'createdBy', label: 'Created By', sortDisabled: true },
    { id: 'action', label: '', sortDisabled: true, width: '72px' },
  ];

  constructor(
    private readonly vesselWoService: VesselWoService,
    private readonly toastrService: ToastrService,
    private readonly authPolicies: AuthPolicies,
  ) {}

  get canManageVesselWoProcessing(): boolean {
    return this.authPolicies.canCreateWorkOrder;
  }

  ngOnInit(): void {
    this.vesselShops = (AuthHelper.user?.maintenanceShops ?? []).filter((shop) => shop.isVesselShop);
    this.getProcessingJobList();
  }

  statusAppearance(status: VesselWoProcessingJobStatus): string {
    return TAG_APPEARANCE_BY_STATUS[status] ?? 'neutral';
  }

  canRefresh(item: VesselWoProcessingJob): boolean {
    return item.status === 'Enqueued' || item.status === 'In Progress';
  }

  onOpenUploadDrawer(): void {
    this.isUploadDrawerOpen = true;
  }

  onUploadDrawerCancelled(): void {
    if (this.isUploadingFile) {
      return;
    }
    this.isUploadDrawerOpen = false;
  }

  onUploadDrawerSaved(payload: { maintenanceShopCode: string; file: File }): void {
    this.isUploadingFile = true;
    this.isUploadDrawerOpen = false;
    this.vesselWoService.uploadFile(payload.maintenanceShopCode, payload.file).subscribe({
      next: (response: any) => {
        this.isUploadingFile = false;
        this.currentPage = 1;
        this.getProcessingJobList();
        const successMessage =
          response?.message ??
          response?.successMessage ??
          response?.data?.message ??
          'File uploaded successfully. Processing has been queued.';
        this.toastrService.showToastr(successMessage, 'success');
      },
      error: (err: any) => {
        this.isUploadingFile = false;
        this.toastrService.showToastr(this.getUploadErrorMessage(err), 'error');
      },
    });
  }

  private getUploadErrorMessage(err: any): string {
    const rawMessage = `${err?.error?.errorMessage ?? err?.error?.message ?? err?.message ?? ''}`.toLowerCase();
    if (rawMessage.includes('maintenance-orders/vessel-wo/files')) {
      return 'Failed to queue the file for processing. Please try again later.';
    }
    return 'Failed to upload the file. Please try again later.';
  }

  getProcessingJobList(): void {
    this.isLoading = true;
    this.vesselWoService.getProcessingJobs(this.currentPage, this.pageLimit).subscribe({
      next: (response) => {
        this.data = response.body ?? [];
        this.setPaginationHeaders(response.headers);
        this.isLoading = false;
      },
      error: () => {
        this.data = [];
        this.isLoading = false;
      },
    });
  }

  onRefreshClicked(item: VesselWoProcessingJob): void {
    this.getProcessingJobList();
  }

  async onDownloadClicked(item: VesselWoProcessingJob): Promise<void> {
    if (!item.reportBlobFileName || item.isDownloading) {
      return;
    }
    item.isDownloading = true;
    try {
      const blob = await this.vesselWoService.downloadReport(item.maintenanceShopCode, item.reportBlobFileName, item.reportBlobFileName);
      downloadBlob(blob, item.reportBlobFileName);
    } catch (err) {
      await handleDownloadError(err, this.toastrService, 'Failed to download the report. Please try again later.');
    } finally {
      item.isDownloading = false;
    }
  }

  onPageChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const changedPage = customEvent.detail as number;
    if (changedPage !== this.currentPage) {
      this.currentPage = changedPage;
      this.getProcessingJobList();
    }
  }

  onPageLimitChange(event: Event): void {
    const customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getProcessingJobList();
  }

  private setPaginationHeaders(headers: { get: (key: string) => string | null }): void {
    this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
    this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
    this.currentPage = +(headers.get('Current-Page') ?? this.currentPage);
  }
}
